const MODE_TO_SEVERITY = {
  outside_range: "critical",
  above: "high",
  below: "medium",
  between: "low",
};

const SEVERITY_RANK = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export const SEVERITY_COLORS = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#eab308",
  low: "#3b82f6",
};

function normalize(str) {
  return String(str || "").trim().toLowerCase();
}

function normalizeSeverityToken(input) {
  const token = String(input == null ? "" : input).trim().toLowerCase();
  if (SEVERITY_RANK[token] != null) return token;
  if (token === "0") return "critical";
  if (token === "1") return "high";
  if (token === "2") return "medium";
  if (token === "3") return "low";
  return "";
}

export function getAlarmSeverity(alarm) {
  const explicitCandidates = [
    alarm?.severity,
    alarm?.priority,
    alarm?.severity_level,
    alarm?.priority_level,
    alarm?.severityLevel,
    alarm?.priorityLevel,
    alarm?.config?.severity,
  ];
  for (const candidate of explicitCandidates) {
    const normalized = normalizeSeverityToken(candidate);
    if (normalized) return normalized;
  }
  return MODE_TO_SEVERITY[normalize(alarm?.mode)] || "low";
}

export function getAlarmDisplayPath(alarm) {
  const explicit =
    alarm?.display_path ??
    alarm?.displayPath ??
    alarm?.source_path ??
    alarm?.sourcePath;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit);
  }
  return String(alarm?.tag_id || "");
}

export function matchesTextFilter(alarm, textFilter) {
  const filter = normalize(textFilter);
  if (!filter) return true;
  const message = normalize(alarm?.message);
  const mode = normalize(alarm?.mode);
  const severity = getAlarmSeverity(alarm);
  const source = normalize(getAlarmDisplayPath(alarm));
  const name = normalize(alarm?.name);
  return (
    message.includes(filter) ||
    mode.includes(filter) ||
    source.includes(filter) ||
    severity.includes(filter) ||
    name.includes(filter)
  );
}

export function sortAlarms(alarms, sortBy = "newest") {
  const list = Array.isArray(alarms) ? alarms.slice() : [];
  const mode = normalize(sortBy || "newest");

  const cmp = (a, b) => {
    switch (mode) {
      case "oldest":
        return new Date(a?.active_at) - new Date(b?.active_at);
      case "priority": {
        const aRank = SEVERITY_RANK[getAlarmSeverity(a)] ?? 999;
        const bRank = SEVERITY_RANK[getAlarmSeverity(b)] ?? 999;
        if (aRank !== bRank) return aRank - bRank;
        return new Date(b?.active_at) - new Date(a?.active_at);
      }
      case "source": {
        const aPath = getAlarmDisplayPath(a);
        const bPath = getAlarmDisplayPath(b);
        return aPath.localeCompare(bPath);
      }
      case "message":
        return String(a?.message || "").localeCompare(String(b?.message || ""));
      case "newest":
      default:
        return new Date(b?.active_at) - new Date(a?.active_at);
    }
  };

  return list.sort(cmp);
}

export const DEFAULT_ROW_STYLES = {
  activeUnacked: {
    base: { bg: "#DB3939", color: "#FAFAFB", fontWeight: "500" },
    critical: { bg: "#DB3939" },
    high: { bg: "#E25353" },
    medium: { bg: "#E86D6D" },
    low: { bg: "#F28787" },
  },
  activeAcked: {
    base: { bg: "#1e293b", color: "#94a3b8" },
    critical: { bg: "#1e293b" },
    high: { bg: "#1e293b" },
    medium: { bg: "#1e293b" },
    low: { bg: "#1e293b" },
  },
  clearUnacked: {
    base: { bg: "#2E5EAA", color: "#FAFAFB", fontWeight: "500" },
    critical: { bg: "#2E5EAA" },
    high: { bg: "#507ABF" },
    medium: { bg: "#7198D6" },
    low: { bg: "#A0BEEF" },
  },
  clearAcked: {
    base: { bg: "#1e293b", color: "#94a3b8" },
    critical: { bg: "#1e293b" },
    high: { bg: "#1e293b" },
    medium: { bg: "#1e293b" },
    low: { bg: "#1e293b" },
  },
};

export function getRowStyle(alarm) {
  const severity = getAlarmSeverity(alarm);
  let stateKey;
  if (alarm?.state === "clear") {
    stateKey = alarm?.acknowledged ? "clearAcked" : "clearUnacked";
  } else {
    stateKey = alarm?.acknowledged ? "activeAcked" : "activeUnacked";
  }

  const stateStyle = DEFAULT_ROW_STYLES[stateKey] || DEFAULT_ROW_STYLES.activeUnacked;
  const base = stateStyle.base || {};
  const sev = stateStyle[severity] || {};

  return {
    bg: sev.bg || base.bg || "#1e293b",
    color: sev.color || base.color || "#e2e8f0",
    fontWeight: sev.fontWeight || base.fontWeight || "normal",
  };
}
